import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, notifyApiError, INR } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import TopBar from "../components/TopBar";
import TableCard from "../components/TableCard";
import StartSessionModal from "../components/StartSessionModal";
import StopSessionModal from "../components/StopSessionModal";
import HistoryDrawer from "../components/HistoryDrawer";
import PlayersPortalCard from "@/components/PlayersPortalCard";
import { toast } from "sonner";

// Merge fresh backend tables into the locally-held ones WITHOUT clobbering
// the running session's `start_time`. Reason: clock skew between client and
// server can make `now − server.start_time` go briefly negative, snapping
// the live timer to 00:00:00. The timer should be driven purely by the
// client clock from the moment the user pressed "Start".
function mergeTables(prev, incoming) {
  const prevById = new Map((prev || []).map((t) => [t.id, t]));
  return incoming.map((t) => {
    const old = prevById.get(t.id);
    const oldSess = old?.current_session;
    const newSess = t.current_session;

    // Case 1: server doesn't yet know about a session we've optimistically
    // created locally (Start request is still in flight). Don't clobber the
    // running card with "available" — keep the local optimistic state.
    if (oldSess && oldSess._optimistic && !newSess) {
      return old;
    }

    // Case 2: same session known to both sides. Keep the local `start_time`
    // (the moment the user pressed Start) so the live timer doesn't rewind
    // due to client↔server clock skew.
    if (
      oldSess &&
      newSess &&
      oldSess.id === newSess.id &&
      oldSess.start_time
    ) {
      return {
        ...t,
        current_session: { ...newSess, start_time: oldSess.start_time },
      };
    }
    return t;
  });
}

export default function Dashboard() {
  const { logout } = useAuth();
  const [tables, setTables] = useState([]);
  const [players, setPlayers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [paymentCfg, setPaymentCfg] = useState({ razorpay_enabled: false });
  const [nowTick, setNowTick] = useState(Date.now());

  const [startFor, setStartFor] = useState(null); // table object
  const [stopFor, setStopFor] = useState(null); // table + session
  const [historyOpen, setHistoryOpen] = useState(false);

  const refresh = async () => {
    try {
      const [t, p, s, c] = await Promise.all([
        api.get("/tables"),
        api.get("/players"),
        api.get("/settings"),
        api.get("/payments/config"),
      ]);
      setTables((prev) => mergeTables(prev, t.data));
      setPlayers(p.data);
      setSettings(s.data);
      setPaymentCfg(c.data);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Live tick every second
  useEffect(() => {
    const i = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // Periodically resync tables with backend so we never act on stale state
  // (e.g. another tablet or admin tool changed things).
  useEffect(() => {
    const i = setInterval(async () => {
      try {
        const { data } = await api.get("/tables");
        setTables((prev) => mergeTables(prev, data));
      } catch (e) {
        console.warn("tables resync failed", e);
      }
    }, 5_000);
    return () => clearInterval(i);
  }, []);

  // Refresh settings periodically (happy-hour transitions, etc.)
  useEffect(() => {
    const i = setInterval(async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (e) {
        console.warn("settings refresh failed", e);
      }
    }, 60_000);
    return () => clearInterval(i);
  }, []);

  // Stable references (never recreated) so TableCard — which is memoized —
  // doesn't re-render every table just because Dashboard re-rendered.
  const handleStartClick = useCallback((t) => setStartFor(t), []);
  const handleStopClick = useCallback((t) => setStopFor(t), []);

  const onStart = async ({ player_name, player_type, player_id, save_player }) => {
    const table = startFor;
    if (!table) return;
    // Optimistic: close modal & flip the table to running instantly so the
    // admin gets zero perceived latency. Backend call runs in the background;
    // on error we revert and surface the error.
    const optimisticSessionId = `optimistic-${Date.now()}`;
    const optimisticStartTime = new Date().toISOString();
    setStartFor(null);
    setTables((prev) =>
      prev.map((t) =>
        t.id === table.id
          ? {
              ...t,
              status: "running",
              current_session_id: optimisticSessionId,
              current_session: {
                id: optimisticSessionId,
                table_id: table.id,
                table_name: table.name,
                player_name: player_name || "Player",
                player_type,
                start_time: optimisticStartTime,
                status: "running",
                _optimistic: true,
              },
            }
          : t,
      ),
    );
    try {
      const { data: realSession } = await api.post("/sessions/start", {
        table_id: table.id,
        player_name,
        player_type,
        player_id: player_id || null,
        save_player: !!save_player,
      });
      // Replace the optimistic placeholder with the real session immediately,
      // so an instant "Stop" click uses the real session id (not "optimistic-…").
      // IMPORTANT: keep the optimistic `start_time` (the exact moment the user
      // pressed Start, in *client* time) so the running timer never rewinds
      // due to client↔server clock skew.
      setTables((prev) =>
        prev.map((t) =>
          t.id === table.id
            ? {
                ...t,
                status: "running",
                current_session_id: realSession.id,
                current_session: { ...realSession, start_time: optimisticStartTime },
              }
            : t,
        ),
      );
      toast.success(`Session started on ${table.name}`);
      refresh();
    } catch (e) {
      // Roll back the optimistic update
      setTables((prev) =>
        prev.map((t) =>
          t.id === table.id
            ? { ...t, status: "available", current_session_id: null, current_session: null }
            : t,
        ),
      );
      notifyApiError(toast, e);
    }
  };

  const onStop = async (extras) => {
    const sid = stopFor?.current_session?.id;
    // Guard against the optimistic-update race: if the start request hasn't
    // returned yet, the session id will be a placeholder ("optimistic-…") and
    // the backend will (correctly) return 404. Tell the admin to wait a beat.
    if (!sid || String(sid).startsWith("optimistic-")) {
      toast.message("Starting session… please try again in a moment.");
      refresh();
      return;
    }

    // ---- Optimistic stop ----
    // Atlas latency makes the `/sessions/:id/stop` round-trip ~1.5–2s. Show
    // the receipt and free up the table *immediately*; reconcile / revert when
    // the backend responds.
    const sess = stopFor.current_session;
    const tableId = stopFor.id;
    const optimisticBreakdown = extras?._optimistic_breakdown;
    // strip the client-only field before sending to backend
    const apiPayload = { ...extras };
    delete apiPayload._optimistic_breakdown;

    const optimisticFinalSession = optimisticBreakdown
      ? {
          ...sess,
          end_time: new Date().toISOString(),
          duration_minutes: optimisticBreakdown.total_minutes,
          amount: optimisticBreakdown.amount,
          breakdown: optimisticBreakdown,
          payment_mode: extras.payment_mode,
          payment_status: extras.payment_status,
          status: "completed",
          player_name:
            extras.player_name && extras.player_name.trim()
              ? extras.player_name.trim()
              : sess.player_name,
        }
      : null;

    // Snapshot the previous table state so we can roll back on failure.
    const prevTables = tables;
    if (optimisticFinalSession) {
      setStopFor({
        ...stopFor,
        finalSession: optimisticFinalSession,
        finalized: true,
      });
      setTables((prev) =>
        prev.map((t) =>
          t.id === tableId
            ? { ...t, status: "available", current_session_id: null, current_session: null }
            : t,
        ),
      );
    }

    try {
      const { data } = await api.post(`/sessions/${sid}/stop`, apiPayload);
      // Reconcile silently with the authoritative backend numbers.
      setStopFor((cur) => (cur ? { ...cur, finalSession: data, finalized: true } : cur));
      toast.success(`Session ended — ${INR(data.amount)}`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already completed")
      ) {
        try {
          const { data } = await api.get(`/sessions/${sid}`);
          toast.message("This session was already closed — showing receipt.");
          setStopFor((cur) => (cur ? { ...cur, finalSession: data, finalized: true } : cur));
          refresh();
          return;
        } catch {
          /* fall through */
        }
      }
      // Roll back the optimistic UI so the admin can retry.
      setTables(prevTables);
      setStopFor((cur) =>
        cur ? { ...cur, finalSession: null, finalized: false } : cur,
      );
      notifyApiError(toast, e);
    }
  };

  const onPayRazorpay = async (preferredMode = "upi", playerPayload = {}) => {
    const sid = stopFor?.current_session?.id;
    if (!sid) return;
    if (String(sid).startsWith("optimistic-")) {
      toast.message("Starting session… please try again in a moment.");
      refresh();
      return;
    }
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) {
      toast.error("Razorpay checkout failed to load");
      return;
    }
    let order;
    try {
      const { data } = await api.post("/payments/orders", { session_id: sid });
      order = data;
    } catch (e) {
      notifyApiError(toast, e);
      return;
    }
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: settings?.club_name || "Cue Lounge",
      description: `${stopFor.name} · ${stopFor.current_session.player_name}`,
      prefill: { name: stopFor.current_session.player_name, method: preferredMode },
      theme: { color: "#d4af37" },
      handler: async (resp) => {
        try {
          const { data } = await api.post(`/sessions/${sid}/stop`, {
            ...playerPayload,
            payment_mode: preferredMode,
            payment_status: "paid",
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success(`Paid via Razorpay — ${INR(data.amount)}`);
          setStopFor({ ...stopFor, finalSession: data, finalized: true });
          refresh();
        } catch (e) {
          notifyApiError(toast, e);
        }
      },
      modal: {
        ondismiss: () => {
          toast.message("Payment cancelled — session is still running");
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(`Payment failed: ${resp.error?.description || "unknown"}`);
    });
    rzp.open();
  };

  const gridCols = useMemo(() => {
    // Always 4 columns on lg, but adapt sm/md responsively
    return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6";
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#0a0f0d] flex flex-col">
      <TopBar
        settings={settings}
        nowTick={nowTick}
        onLogout={logout}
        onOpenHistory={() => setHistoryOpen(true)}
      />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-5 lg:py-6">
        {tables.length === 0 ? (
          <div className="text-center text-gray-500 py-24" data-testid="no-tables">
            No tables yet.
          </div>
        ) : (
          <div className={gridCols} data-testid="tables-grid">
            {tables.map((t, idx) => (
              <TableCard
                key={t.id}
                table={t}
                index={idx + 1}
                settings={settings}
                onStart={handleStartClick}
                onStop={handleStopClick}
              />
            ))}
          </div>
        )}

        <PlayersPortalCard />
      </main>

      <StartSessionModal
        open={!!startFor}
        table={startFor}
        onClose={() => setStartFor(null)}
        onSubmit={onStart}
      />
      <StopSessionModal
        open={!!stopFor}
        tableSession={stopFor}
        settings={settings}
        paymentCfg={paymentCfg}
        players={players}
        nowTick={nowTick}
        onClose={() => {
          setStopFor(null);
        }}
        onConfirm={onStop}
        onPayRazorpay={onPayRazorpay}
      />
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        settings={settings}
      />
    </div>
  );
}
